// signalYAxisPan.ts
import { axisLeft, drag, type NumberValue, type ScaleLinear, type D3DragEvent } from 'd3';

import type { SafeAny } from '@/common/types';
import type { GraphPoint, Selection } from '@/common/ui';

import type { SignalAmplitudeRule } from '.';
import { signalAmplitudeRules } from '../signalAmplitudeRules';
import { INVERTIBLE_SIGNAL_TITLES } from '../signal-graph.d3';

// Fallback rule
const FALLBACK_AMPLITUDE_RULE: SignalAmplitudeRule = {
  min: -2000,
  max: 2000,
  absoluteMin: -2000,
  absoluteMax: 2000,
  step: 50,
  symmetric: true,
  unit: ''
};

export function getAmplitudeRuleForTitle(title: string): SignalAmplitudeRule {
  return signalAmplitudeRules[title] ?? FALLBACK_AMPLITUDE_RULE;
}

function getAbsoluteBounds(rule: SignalAmplitudeRule): { absMin: number; absMax: number } {
  const absMin = toNumberOrUndefined(rule.absoluteMin) ?? toNumberOrUndefined(rule.min) ?? 0;
  const absMax = toNumberOrUndefined(rule.absoluteMax) ?? toNumberOrUndefined(rule.max) ?? 0;

  return { absMin, absMax };
}

export interface YAxisPanContext {
  title: string;

  svg: Selection;
  svgSizes: { width: number; height: number };
  graphSettings: {
    chartLayout: {
      margins: {
        top: number;
        left: number;
        right: number;
        yAxis: number;
        title: number;
        zoomComponent: number;
      };
    };
  };

  yAxisGroup: Selection;
  graphGroup: Selection;
  yAxisDragZone?: Selection;

  mapping: { min: number; max: number };
  _dragStartY: number | null;
  _initialMapping: { min: number; max: number } | null;

  xScale: ScaleLinear<number, number>;
  yScale: ScaleLinear<number, number>;
  data: GraphPoint<SafeAny>[];

  getInvertedY(value: number): number;
  getYTickFormat(d: NumberValue): string;
  updateScales(): void;
  updateAxis(): void;
  animateRedraw(duration?: number): void;
}

export function setupYAxisDrag(ctx: YAxisPanContext): void {
  if (!ctx.yAxisGroup) return;

  const rule = getAmplitudeRuleForTitle(ctx.title);
  const { absMin, absMax } = getAbsoluteBounds(rule);

  if (!signalAmplitudeRules[ctx.title]) {
    ctx.yAxisGroup.on('.drag', null);
    ctx.yAxisDragZone?.remove?.();
    return;
  }

  let rafId: number | null = null;

  const updateDuringDrag = () => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      ctx.updateScales();

      ctx.yAxisGroup.interrupt().call(
        axisLeft(ctx.yScale)
          .tickFormat((d) => ctx.getYTickFormat(d))
          .tickValues([ctx.mapping.min, ctx.mapping.max])
          .tickSizeOuter(0)
          .tickSizeInner(0)
      );

      ctx.yAxisGroup
        .selectAll('.tick text')
        .style('font-size', '9px')
        .style('color', 'black')
        .style('font-weight', 'normal');

      ctx.updateAxis();
    });
  };

  const dragBehavior = drag()
    .on('start', (event: D3DragEvent<any, any, any>) => {
      ctx.yAxisDragZone?.style('cursor', 'grabbing');
      ctx._dragStartY = event.y;
      ctx._initialMapping = { ...ctx.mapping };
    })
    .on('drag', (event: D3DragEvent<any, any, any>) => {
      if (ctx._dragStartY === null || !ctx._initialMapping) return;

      const deltaY = event.y - ctx._dragStartY;

      const directionMultiplier =
        INVERTIBLE_SIGNAL_TITLES.has(ctx.title?.toLowerCase()) && (ctx as any).polarityInversion
          ? -1
          : 1;

      const adjustedDeltaY = deltaY * directionMultiplier;

      const pixelRange = ctx.svgSizes.height - ctx.graphSettings.chartLayout.margins.top * 2;

      const initialMin = ctx._initialMapping.min;
      const initialMax = ctx._initialMapping.max;
      const windowSize = initialMax - initialMin;

      const valuePerPixel = windowSize / pixelRange;
      const deltaValue = adjustedDeltaY * valuePerPixel;

      let newMin = initialMin + deltaValue;
      let newMax = newMin + windowSize;

      if (newMin < absMin) {
        newMin = absMin;
        newMax = absMin + windowSize;
      }
      if (newMax > absMax) {
        newMax = absMax;
        newMin = absMax - windowSize;
      }

      ctx.mapping.min = newMin;
      ctx.mapping.max = newMax;

      updateDuringDrag();
    })
    .on('end', () => {
      if (rafId) cancelAnimationFrame(rafId);
      ctx.yAxisDragZone?.style('cursor', 'grab');
      ctx._dragStartY = null;
      ctx._initialMapping = null;
      ctx.animateRedraw();
      ctx.updateAxis();

      // Persist the final window into Vuex so FE/BE stay in sync
      (ctx as any)?._onYAxisPanEnd?.({
        min: ctx.mapping.min,
        max: ctx.mapping.max
      });
    });

  const MIN_DRAG_ZONE = 60;
  const MAX_DRAG_ZONE = 100;
  const REQUIRED_GRAPH_WIDTH = 1200;

  // How much space remains for the left UI area (axis + title + drag zone)
  const spaceLeft = ctx.svgSizes.width - REQUIRED_GRAPH_WIDTH;

  // Your fixed UI width (axis labels + margins + title offset)
  const FIXED_LEFT_UI =
    ctx.graphSettings.chartLayout.margins.left +
    ctx.graphSettings.chartLayout.margins.yAxis +
    ctx.graphSettings.chartLayout.margins.title;

  // Calculate remaining space for the drag zone
  let dragZoneWidth = spaceLeft - FIXED_LEFT_UI;

  // Clamp to safe bounds
  dragZoneWidth = Math.max(MIN_DRAG_ZONE, Math.min(MAX_DRAG_ZONE, dragZoneWidth));

  // Prevent negative width in extreme edge cases
  if (dragZoneWidth < MIN_DRAG_ZONE) dragZoneWidth = MIN_DRAG_ZONE;
  // -----------------------------------------------------------------

  const dragZoneHeight = ctx.svgSizes.height - ctx.graphSettings.chartLayout.margins.top * 2;

  const dragZoneX = 0;
  const dragZoneY = ctx.graphSettings.chartLayout.margins.top;

  ctx.yAxisDragZone?.remove?.();

  ctx.yAxisDragZone = ctx.svg
    .insert('rect', '.y-axis')
    .attr('class', 'y-axis-drag-zone')
    .attr('x', dragZoneX)
    .attr('y', dragZoneY)
    .attr('width', dragZoneWidth)
    .attr('height', dragZoneHeight)
    .style('fill', 'transparent')
    .style('cursor', 'grab');

  ctx.yAxisDragZone.raise();
  ctx.yAxisDragZone.call(dragBehavior as any);
}

function toNumberOrUndefined(value: number | 'auto' | undefined): number | undefined {
  return typeof value === 'number' ? value : undefined;
}
